import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { getEmailLogs, retryEmail } from '../../redux/slices/emailLogSlice';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import alertify from 'alertifyjs';

/**
 * EmailLogsPage - Admin email logs page
 * 
 * Features:
 * - Email log table with filtering (status, type, date range)
 * - Retry failed emails
 * - Status chips (pending, sent, failed, delivered, bounced)
 * - Pagination
 * 
 * Permission: admin.email-logs.view
 */
const EmailLogsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { emailLogs, loading, error, pagination } = useSelector(
    (state) => state.emailLogs
  );

  const hasPermission = useHasPermission('admin.email-logs.view');
  const hasRetryPermission = useHasPermission('admin.email-logs.retry');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [retryingId, setRetryingId] = useState(null);
  const [viewDialog, setViewDialog] = useState({ open: false, emailLog: null });

  // Debounce search term (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch email logs
  useEffect(() => {
    if (hasPermission) {
      const params = {
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        to: debouncedSearch || undefined,
        page: page + 1,
        limit,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      dispatch(getEmailLogs(params));
    }
  }, [dispatch, statusFilter, typeFilter, startDate, endDate, debouncedSearch, hasPermission, page, limit]);

  // Handle retry email
  const handleRetryEmail = async (emailLogId) => {
    if (!hasRetryPermission) {
      alertify.error('Bu işlem için yetkiniz yok');
      return;
    }

    setRetryingId(emailLogId);
    try {
      await dispatch(retryEmail(emailLogId)).unwrap();
      alertify.success('Email başarıyla tekrar gönderildi');
      // Refresh email logs
      dispatch(getEmailLogs({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        to: debouncedSearch || undefined,
        page: page + 1,
        limit,
      }));
    } catch (error) {
      alertify.error(error.message || 'Email tekrar gönderilemedi');
    } finally {
      setRetryingId(null);
    }
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'sent':
        return 'info';
      case 'failed':
        return 'error';
      case 'delivered':
        return 'success';
      case 'bounced':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status label (Turkish)
  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'sent':
        return 'Gönderildi';
      case 'failed':
        return 'Başarısız';
      case 'delivered':
        return 'Teslim Edildi';
      case 'bounced':
        return 'Geri Döndü';
      default:
        return status;
    }
  };

  // Get type label (Turkish)
  const getTypeLabel = (type) => {
    switch (type) {
      case 'password_reset':
        return 'Şifre Sıfırlama';
      case 'verification':
        return 'Email Doğrulama';
      case 'invite_new':
        return 'Davet (Yeni Kullanıcı)';
      case 'invite_existing':
        return 'Davet (Mevcut Kullanıcı)';
      case 'account_created':
        return 'Hesap Oluşturuldu';
      case 'password_reset_confirmation':
        return 'Şifre Değişikliği Onayı';
      default:
        return type;
    }
  };

  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => history.goBack()}>
            <ArrowBackIcon />
          </IconButton>
          <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Email Logları
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email gönderim geçmişi ve durumu
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Email adresi ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Durum"
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="pending">Beklemede</MenuItem>
                <MenuItem value="sent">Gönderildi</MenuItem>
                <MenuItem value="failed">Başarısız</MenuItem>
                <MenuItem value="delivered">Teslim Edildi</MenuItem>
                <MenuItem value="bounced">Geri Döndü</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Tip</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Tip"
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="password_reset">Şifre Sıfırlama</MenuItem>
                <MenuItem value="verification">Email Doğrulama</MenuItem>
                <MenuItem value="invite_new">Davet (Yeni Kullanıcı)</MenuItem>
                <MenuItem value="invite_existing">Davet (Mevcut Kullanıcı)</MenuItem>
                <MenuItem value="account_created">Hesap Oluşturuldu</MenuItem>
                <MenuItem value="password_reset_confirmation">Şifre Değişikliği Onayı</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Başlangıç Tarihi"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Bitiş Tarihi"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Alıcı</TableCell>
                <TableCell>Konu</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Gönderilme Tarihi</TableCell>
                <TableCell>Tekrar Deneme</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : emailLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Email log bulunamadı
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                emailLogs.map((log) => (
                  <TableRow key={log._id}>
                    <TableCell>{log.to}</TableCell>
                    <TableCell>{log.subject}</TableCell>
                    <TableCell>{getTypeLabel(log.type)}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(log.status)}
                        color={getStatusColor(log.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {log.sentAt
                        ? format(new Date(log.sentAt), 'dd MMM yyyy HH:mm', { locale: tr })
                        : '-'}
                    </TableCell>
                    <TableCell>{log.retryCount || 0}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {log.htmlContent && (
                          <Tooltip title="HTML İçeriğini Görüntüle">
                            <IconButton
                              size="small"
                              onClick={() => setViewDialog({ open: true, emailLog: log })}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {log.status === 'failed' && hasRetryPermission && (
                          <Tooltip title="Tekrar Gönder">
                            <IconButton
                              size="small"
                              onClick={() => handleRetryEmail(log._id)}
                              disabled={retryingId === log._id}
                            >
                              {retryingId === log._id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <RefreshIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pagination.total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Sayfa başına:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / ${count !== -1 ? count : `çok fazla`}`
            }
          />
        </TableContainer>

        {/* HTML Content View Dialog */}
        <Dialog
          open={viewDialog.open}
          onClose={() => setViewDialog({ open: false, emailLog: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Email İçeriği - {viewDialog.emailLog?.subject}
          </DialogTitle>
          <DialogContent>
            {viewDialog.emailLog?.htmlContent ? (
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: '70vh',
                  overflow: 'auto',
                  '& img': { maxWidth: '100%' }
                }}
                dangerouslySetInnerHTML={{ __html: viewDialog.emailLog.htmlContent }}
              />
            ) : (
              <Typography color="text.secondary">
                HTML içeriği bulunamadı
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialog({ open: false, emailLog: null })}>
              Kapat
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default EmailLogsPage;

